import UnlockIcon from '@mui/icons-material/AutoAwesome'
import { Alert, Box, MenuItem, TextField } from '@mui/material'
import { pickBy } from 'lodash'
import { useTranslate } from 'ra-core'
import { useState } from 'react'
import { Title } from 'react-admin'

import CustomKpisRequest from './CustomKpisRequest'
import UtilisationOverview from './UtilisationOverview'
import MetabaseEmbed from '../../components/MetabaseEmbed'
import { COMMON_INPUT_WIDTH } from '../../config/theme'
import { ShowCard } from '../common/show'
import { useGetHubsListForOrganisation } from '../hubs/hooks'
import { useGetOrganisations } from '../organisations/hooks'

const ALL_ORGANISATIONS = 'all'
const ALL_HUBS = 'all'
const LAST_3_MONTHS = 'last3Months'

const filterInputSx = { m: 0, width: { xs: '100%', sm: COMMON_INPUT_WIDTH } }

const StatisticsContent = ({ restrictedOrganisationId }) => {
  const translate = useTranslate()
  const { data: organisations = [] } = useGetOrganisations({ pagination: { page: 1, perPage: 100 } })
  const [selectedOrganisation, setSelectedOrganisation] = useState(ALL_ORGANISATIONS)
  const hasSingleOrganisation = organisations.length === 1
  const isOrganisationLocked = Boolean(restrictedOrganisationId) || hasSingleOrganisation
  const organisationValue =
    restrictedOrganisationId ?? (hasSingleOrganisation ? organisations[0].id : selectedOrganisation)
  const organisationId = organisationValue === ALL_ORGANISATIONS ? undefined : organisationValue
  const { data: hubs = [] } = useGetHubsListForOrganisation(organisationId)
  const [selectedHub, setSelectedHub] = useState(ALL_HUBS)
  const hasSingleHub = hubs.length === 1
  const hubValue = hasSingleHub ? hubs[0].id : selectedHub
  const hubId = hubValue === ALL_HUBS ? undefined : hubValue

  const onOrganisationChange = (event) => {
    setSelectedOrganisation(event.target.value)
    setSelectedHub(ALL_HUBS)
  }

  return (
    <Box mb={3}>
      <Title title="mymove.statistics.name" />
      <Box mt={2} px={{ xs: 2, sm: 0 }} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <TextField
          select
          size="small"
          label={translate('resources.organisations.name', 1)}
          value={organisationValue}
          disabled={isOrganisationLocked}
          onChange={onOrganisationChange}
          sx={filterInputSx}
        >
          {!isOrganisationLocked && (
            <MenuItem value={ALL_ORGANISATIONS}>{translate('mymove.statistics.allOrganisations')}</MenuItem>
          )}
          {organisations.map((organisation) => (
            <MenuItem key={organisation.id} value={organisation.id}>
              {organisation.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={translate('resources.hubs.name', 1)}
          value={hubValue}
          disabled={!organisationId || hasSingleHub}
          onChange={(event) => setSelectedHub(event.target.value)}
          sx={filterInputSx}
        >
          {!hasSingleHub && <MenuItem value={ALL_HUBS}>{translate('mymove.statistics.allHubs')}</MenuItem>}
          {hubs.map((hub) => (
            <MenuItem key={hub.id} value={hub.id}>
              {hub.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={translate('mymove.statistics.period')}
          value={LAST_3_MONTHS}
          disabled
          sx={filterInputSx}
        >
          <MenuItem value={LAST_3_MONTHS}>{translate('mymove.statistics.last3Months')}</MenuItem>
        </TextField>
        <Box sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}>
          <CustomKpisRequest fullWidthOnMobile />
        </Box>
      </Box>
      <ShowCard>
        <MetabaseEmbed
          path="/statistics/embeds/dashboard"
          params={pickBy({ organisation_id: organisationId, hub_id: hubId })}
          title={translate('mymove.statistics.name')}
        />
      </ShowCard>
      <Alert
        severity="success"
        icon={<UnlockIcon />}
        sx={{ mt: 2, mx: { xs: 2, sm: 0 }, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          gap={2}
        >
          <Box sx={{ whiteSpace: 'pre-line' }}>{translate('mymove.statistics.customKpis.teaser')}</Box>
          <CustomKpisRequest variant="outlined" size="small" fullWidthOnMobile />
        </Box>
      </Alert>
      {organisationId ? (
        <UtilisationOverview organisationId={organisationId} hubId={hubId} />
      ) : (
        <Alert severity="info" sx={{ mt: 3 }}>
          {translate('mymove.statistics.utilisationRateNeedsOrganisation')}
        </Alert>
      )}
    </Box>
  )
}

export default StatisticsContent
